import React from "react";
import { useState, useEffect, useContext, useRef } from "react";
import { plagiarismContext } from "./PlagiarismMain";
import {
  ALERT_TIMEOUT,
  MIN_AI_TEXT_LENGTH,
  STATUS_CHECK_INTERVAL,
  ThemeButtonPlagiarismBG,
} from "../Config";
import { LoggedInContext } from "../../App";
import { REQUEST_URL } from "../Config";
import { formatSecondsToMS } from "../Utils";
import { useNavigate } from "react-router";

function PlagiarismCheckerContent() {
  //navigate
  const navigate = useNavigate(); //react router navigate

  //time to show time value
  //initialise with 2 minutes
  const [time, setTime] = useState(60 * 2);

  //get ai detector values
  const {
    plagiarismDetectorValues,
    setPlagiarismDetectorValues,
    detectorResultSectionRef,
  } = useContext(plagiarismContext);

  //alert state
  const [alert, setAlert] = useState({
    type: "success",
    message: "",
    visible: false,
  });

  //
  //get ai detector values
  // eslint-disable-next-line no-unused-vars
  const { maxAITextLength } = useContext(LoggedInContext);

  //delay time
  //delay to run the code in runpod server i.e in queue time
  //execution time
  //time taken to execute the code
  //total time is = delayTime + executionTime
  var process_time = useRef({
    delayTime: 0.0,
    executionTime: 0.0,
  });

  //submit form button clicked state
  const [submitClicked, setSubmitClicked] = useState(false);
  //result loadedstate
  const [resultLoaded, setResultLoaded] = useState(false);

  //scroll to section on result
  const scrollToSection = () => {
    if (detectorResultSectionRef.current) {
      detectorResultSectionRef.current.scrollIntoView({
        behavior: "smooth", // Optional: for smooth scrolling animation
        block: "start", // Optional: aligns the top of the element with the top of the viewport
      });
    }
  };

  //timer id
  const timerId = useRef();

  //use effect to handle the submit button click
  useEffect(() => {
    // If the submit button was clicked
    if (submitClicked) {
      //reset
      process_time.delayTime = 0.0;
      process_time.executionTime = 0.0;

      //call plagiarism checker
      plagiarismDetectorAPICall();

      //time for time remaiming
      timerId.current = setInterval(
        () => setTime((time) => Math.max(0, time - 1)),
        1000
      );
    } else {
      //clear
      clearInterval(timerId.current);
      //reset
      setTime(60 * 2);
    }

    //if result loaded
    if (resultLoaded === true) {
      //update search history
      updateSearchHistory();
    }

    //cleanup
    return () => clearInterval(timerId); // Cleanup on unmount

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitClicked, resultLoaded]);

  //check status from status from endpoint
  const checkRunpodJobStatus = async (job_id) => {
    //get the access token from local storage
    var accessToken = localStorage.getItem("access_token");
    accessToken =
      accessToken !== null && accessToken !== "undefined" ? accessToken : "";

    //fetch the status from the API
    await fetch(REQUEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`, // if needed
      },
      body: JSON.stringify({
        request_type: "STATUS_CHECK_PLAGIARISM_DETECTOR",
        text_to_process: "",
        job_id: job_id, // can be used for tracking jobs
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        //check success
        if (data.success === true) {
          //check status
          if (data.status === "COMPLETED") {
            //hide the spinner
            setSubmitClicked(false);

            //result json
            const result = JSON.parse(data.message);
            // Update the state with the new value
            setPlagiarismDetectorValues({
              ...plagiarismDetectorValues,
              probability: result.probability,
              is_plagiarism: result.probability > 0.5 ? 0 : 1,
              text: result.text,
              isLoaded: true,
            });

            //set time
            process_time.delayTime = data.delay_time;
            process_time.executionTime = data.execution_time;

            //scroll
            scrollToSection();
            //set
            setResultLoaded(true);
          } else if (
            data.status === "FAILED" ||
            data.status === "CANCELLED" ||
            data.status === "TIMED_OUT"
          ) {
            //hide the spinner
            setSubmitClicked(false);

            //set alert state to show error
            setAlert({
              type: "danger",
              message: "Failed to detect plagiarism. Please try again.",
              visible: true,
            });
            //hide alert after 5 seconds
            setTimeout(() => {
              setAlert({ ...alert, visible: false });
            }, ALERT_TIMEOUT);
          } else if (
            data.status === "IN_QUEUE" ||
            data.status === "RUNNING" ||
            data.status === "IN_PROGRESS"
          ) {
            //check api after 10 seconds
            setTimeout(() => {
              //callback
              checkRunpodJobStatus(job_id);
            }, STATUS_CHECK_INTERVAL);
          }
        } else {
          //hide the spinner
          setSubmitClicked(false);
          //scroll to top
          window.scrollTo(0, 0);
          //set alert state to show error
          setAlert({
            type: "danger",
            message:
              data.message || "Failed to detect plagiarism. Please try again.",
            visible: true,
          });
          //hide alert after 5 seconds
          setTimeout(() => {
            setAlert({ ...alert, visible: false });
          }, ALERT_TIMEOUT);
        }

        console.log("Response Data plagiarism detector:", data);
      })
      .catch((error) => {
        console.error("Error:", error);
        //hide the spinner
        setSubmitClicked(false);

        //set alert state to show error
        setAlert({
          type: "danger",
          message: "Failed to detect plagiarism. Please try again.",
          visible: true,
        });
        //hide alert after 5 seconds
        setTimeout(() => {
          setAlert({ ...alert, visible: false });
        }, ALERT_TIMEOUT);
      });
  };

  //plagiarism detect texts from endpoint
  const plagiarismDetectorAPICall = async () => {
    //get the access token from local storage
    var accessToken = localStorage.getItem("access_token");
    accessToken =
      accessToken !== null && accessToken !== "undefined" ? accessToken : "";

    //fetch the humanized text from the API
    await fetch(REQUEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`, // if needed
      },
      body: JSON.stringify({
        request_type: "PLAGIARISM_DETECTOR",
        text_to_process: plagiarismDetectorValues.enter_ai_text,
        mode: 1,
        job_id: null, // can be used for tracking jobs
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        //set token in local storage
        localStorage.setItem("access_token", data.token);

        //check if the response is successful
        //and job is send to runpod server
        if (data.success === true) {
          //set job_id
          const job_id = data.job_id;
          //check response from runpod server
          //when ai detection text is completed
          if (
            data.status === "FAILED" ||
            data.status === "CANCELLED" ||
            data.status === "TIMED_OUT" ||
            job_id === null
          ) {
            //hide the spinner
            setSubmitClicked(false);

            //set alert state to show error
            setAlert({
              type: "danger",
              message: "Failed to detect plagiarism. Please try again.",
              visible: true,
            });
            //hide alert after 5 seconds
            setTimeout(() => {
              setAlert({ ...alert, visible: false });
            }, ALERT_TIMEOUT);
          } else {
            //check status for job_id
            checkRunpodJobStatus(job_id);
          }
        } else {
          //hide the spinner
          setSubmitClicked(false);
          //scroll to top
          window.scrollTo(0, 0);
          //set alert state to show error
          setAlert({
            type: "danger",
            message:
              data.message || "Failed to detect plagiarism. Please try again.",
            visible: true,
          });
          //hide alert after 5 seconds
          setTimeout(() => {
            setAlert({ ...alert, visible: false });
          }, ALERT_TIMEOUT);
        }

        console.log("Response Data plagiarism detector:", data);
      })
      .catch((error) => {
        console.error("Error:", error);
        //hide the spinner
        setSubmitClicked(false);
        //set alert state to show error
        setAlert({
          type: "danger",
          message:
            "Failed to detect plagiarism. Please try again. \n" + error.message,
          visible: true,
        });

        //hide
        setTimeout(() => {
          //hide alert after 5 seconds
          setAlert({ ...alert, visible: false });
        }, ALERT_TIMEOUT);
      });
  };

  //update search history
  async function updateSearchHistory() {
    //get the access token from local storage
    var accessToken = localStorage.getItem("access_token");
    accessToken =
      accessToken !== null && accessToken !== "undefined" ? accessToken : "";

    //fetch the humanized text from the API
    await fetch(REQUEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`, // if needed
      },
      body: JSON.stringify({
        request_type: "INSERT_SEARCH_TO_HISTORY",
        text_searched: plagiarismDetectorValues.enter_ai_text,
        text_result: `${(plagiarismDetectorValues.probability * 100).toFixed(
          2
        )}`,
        delay_time: process_time.delayTime,
        execution_time: process_time.executionTime,
        search_type: "PLAGIARISM_DETECTOR",
      }),
    })
      .then((result) => result.json())
      .then((data) => {
        //reset
        setResultLoaded(false);

        console.log("Search history result: ", data);
      })
      .catch((error) => {
        //reset
        setResultLoaded(false);
      });

    console.log("Update Search History");
  }

  //submit handler for the form
  const onSubmitPlagiarismDetectorForm = (event) => {
    // Get the textarea value
    event.preventDefault();

    //check if empty string
    if (plagiarismDetectorValues.enter_ai_text.length <= MIN_AI_TEXT_LENGTH) {
      //show error
      //set alert state to show error
      setAlert({
        type: "danger",
        message: "Too short text. Please enter at least 40 characters.",
        visible: true,
      });
      //hide alert after 5 seconds
      setTimeout(() => {
        setAlert({ ...alert, visible: false });
      }, ALERT_TIMEOUT);
      //return
      return;
    }

    //check text count
    if (plagiarismDetectorValues.enter_ai_text.length > maxAITextLength) {
      //show warning
      //set alert state to show error
      setAlert({
        type: "warning",
        message:
          "Max character limit exceeded. Input texts will be trimmed before processing.",
        visible: true,
      });
      //hide alert after 5 seconds
      setTimeout(() => {
        setAlert({ ...alert, visible: false });
      }, ALERT_TIMEOUT);

      // Update the state with the new value
      setPlagiarismDetectorValues({
        ...plagiarismDetectorValues,
        enter_ai_text: plagiarismDetectorValues.enter_ai_text.substring(
          0,
          maxAITextLength
        ),
      });
    }

    //set the submit clicked state to true
    //can not access the form values directly here
    //as the state is not updated immediately
    //use effect will be called after the state is updated
    setSubmitClicked(true);
  };

  //onvalue change handler for the textarea
  const onChangePlagiarismDetectorTextsForm = (event) => {
    // Update the state with the new value
    setPlagiarismDetectorValues({
      ...plagiarismDetectorValues,
      [event.target.id]: event.target.value,
    });
  };

  return (
    <div className="p-3">
      <div className="container">
        <div style={{ marginTop: "6rem" }}>
          {/* Alert for failure */}
          <div
            className={`alert alert-${alert.type} ${
              alert.visible ? "" : "d-none"
            } fade show`}
            role="alert"
            style={{ marginTop: "5rem" }}
          >
            {alert.message}
          </div>

          <h1 className="display-6 text-center mt-2">Plagiarism Checker</h1>
          <p className="text-center lead mt-4">
            A plagiarism checker is an essential online tool that detects
            duplicate content by comparing your text against billions of web
            pages and academic sources. This free plagiarism checker ensures
            content originality, helping students, writers, and SEO
            professionals avoid unintentional plagiarism. With fast scanning,
            detailed reports, and high accuracy, it’s perfect for maintaining
            100% unique content. Improve your SEO rankings, protect your
            credibility, and stay compliant with copyright standards using our
            advanced plagiarism detection tool.
            <strong>
              This plagiarism checker tool is the best plagiarism detector
              available for free online.
            </strong>
          </p>

          <form onSubmit={onSubmitPlagiarismDetectorForm}>
            <div className="container mt-5">
              <div className="position-relative mt-3">
                {/* Waiting time */}
                <div
                  className={`position-absolute ${
                    submitClicked ? "" : "d-none"
                  }`}
                  style={{
                    left: "0px",
                    right: "0px",
                    margin: "auto",
                    top: "0px",
                    bottom: "0px",
                    width: "200px",
                    height: "100px",
                  }}
                >
                  {/*Gray box */}
                  <div className="bg-secondary rounded-3 p-1">
                    <label
                      className="fs-5 text-light text-center"
                      style={{ width: "100%" }}
                    >
                      Waiting time:
                    </label>
                    <label
                      className="fs-3 text-light text-center"
                      style={{ width: "100%" }}
                    >
                      {formatSecondsToMS(time)}
                    </label>
                  </div>
                  {/* Text upgrade */}
                  <div className="bg-white bg-opacity-10">
                    <label className="text-muted text-center mt-2">
                      <span>
                        <button
                          className="btn btn-light"
                          type="button"
                          onClick={(e) => {
                            //blur
                            e.currentTarget.blur();
                            //go to pricing page
                            navigate("/pricing"); //Navigate to pricing page
                          }}
                        >
                          Upgrade
                        </button>
                      </span>{" "}
                      for quicker response time
                    </label>
                  </div>
                </div>

                <textarea
                  className="form-control m-2 shadow"
                  id="enter_ai_text"
                  rows="18"
                  style={{ resize: "none" }}
                  placeholder="Insert AI generated (English) text here..."
                  onChange={onChangePlagiarismDetectorTextsForm}
                  value={plagiarismDetectorValues.enter_ai_text}
                ></textarea>
                <p
                  className="text-center position-absolute ms-4 mb-2 p-2 bg-light rounded"
                  style={{ bottom: "0px" }}
                >
                  <span
                    className={`${
                      plagiarismDetectorValues.enter_ai_text.length <=
                      MIN_AI_TEXT_LENGTH
                        ? "text-danger"
                        : ""
                    }`}
                  >
                    {plagiarismDetectorValues.enter_ai_text.length}
                  </span>{" "}
                  / {maxAITextLength} Characters
                </p>

                {/*  paste from clipboard */}
                <div
                  className="position-absolute me-2 mb-3 d-flex bg-light"
                  style={{ right: "0px", bottom: "0px" }}
                >
                  <button
                    className="btn btn-outline-success btn-sm me-2"
                    style={{ right: "0px", bottom: "0px" }}
                    type="button"
                    onClick={(e) => {
                      //text from clipboard
                      navigator.clipboard
                        .readText()
                        .then((text) => {
                          //set text
                          setPlagiarismDetectorValues({
                            ...plagiarismDetectorValues,
                            enter_ai_text: text,
                          });
                        })
                        .catch((err) => {
                          console.error(
                            "Failed to read clipboard contents: ",
                            err
                          );
                        });
                      // Remove focus after click
                      e.currentTarget.blur();
                    }}
                  >
                    <i className="bi bi-clipboard me-lg-2 me-sm-0"></i>
                    <span className="d-none d-sm-inline">Paste Texts</span>
                  </button>

                  {/* clear text area */}
                  <button
                    className="btn btn-outline-danger btn-sm"
                    style={{ right: "0px", top: "0px" }}
                    type="button"
                    onClick={(e) => {
                      //clear
                      setPlagiarismDetectorValues({
                        enter_ai_text: "",
                        probability: 0,
                        text: "",
                        isLoaded: false,
                      });
                      // Remove focus after click
                      e.currentTarget.blur();
                    }}
                  >
                    <i className="bi bi-trash3 me-lg-2 me-sm-0"></i>
                    <span className="d-none d-sm-inline">Clear Text</span>
                  </button>
                </div>
              </div>
            </div>

            {/* button to detect ai generated text */}
            <div className="text-center mt-5 justify-content-center d-flex">
              <div
                className={`spinner-border text-dark me-3 align-self-center ${
                  submitClicked ? "visible" : "invisible"
                }`}
                role="status"
              >
                <span className="sr-only"></span>
              </div>
              <button
                className="btn btn-success px-4 py-3 fs-5"
                disabled={submitClicked}
                type="submit"
                style={{
                  backgroundColor: submitClicked
                    ? "#6c757d"
                    : ThemeButtonPlagiarismBG,
                }}
                onClick={(e) => {
                  //blur
                  e.currentTarget.blur();
                }}
              >
                <i className="bi bi-file-text me-3"></i>
                Check Text
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PlagiarismCheckerContent;
